/**
 * LLM-generated follow-up questions for the latest project reply,
 * rendered as pickable line items (mirrors Evidence's ``FollowUpChips``).
 *
 * Replaces the previous static chip set: the questions are produced by
 * the backend (``POST /projects/{id}/follow-up-questions`` → Gemini)
 * from the actual conversation, so they're contextual rather than a
 * fixed "Add to note / Create a task / …" list. Clicking a row auto-sends
 * via ``onSelect`` (same as Evidence follow-up chips).
 *
 * Fetched lazily — only while ``enabled`` (the user opened the
 * Follow-up panel) — and cached per reply by ``useProjectFollowUpQuestions``.
 */

import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { CornerDownRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ProjectFollowUpQuestion } from '../../../contracts/projects/requests.types';
import { useProjectFollowUpQuestions } from '../../../data/projects/hooks';
import {
  dismissRoutineSuggestionForProject,
  isRoutineSuggestionDismissedForProject,
} from '../../../data/projects/lib/routineSuggestionDismiss';
import {
  MAKE_AS_WORKFLOW_COMPOSER_PREFILL,
  UPDATE_WORKFLOW_COMPOSER_PREFILL,
} from '../../../data/workflows/makeAsWorkflowPrompt';
import {
  RoutineSuggestionCard,
  type RoutineSuggestionMode,
} from './RoutineSuggestionCard';

export interface ProjectFollowUpQuestionsProps {
  projectId: string;
  /** Identity of the reply being followed up on (drives per-reply cache). */
  replyId: string;
  /** Only fetch + render while the Follow-up panel is open. */
  enabled: boolean;
  /** Auto-send the chosen follow-up question (via ProjectChat → submitPrompt). */
  onSelect: (question: string) => void;
  className?: string;
}

const ROUTINE_CARD_TYPES = new Set(['workflow_capture', 'workflow_update']);

const resolveRoutineSuggestionMode = (
  questions: ProjectFollowUpQuestion[]
): RoutineSuggestionMode | null => {
  if (questions.some((question) => question.type === 'workflow_update')) {
    return 'update';
  }
  if (questions.some((question) => question.type === 'workflow_capture')) {
    return 'create';
  }
  return null;
};

export const ProjectFollowUpQuestions = ({
  projectId,
  replyId,
  enabled,
  onSelect,
  className,
}: ProjectFollowUpQuestionsProps) => {
  // Read dismissals eagerly so a remount after "Not now" never flashes the card.
  const [routineSuggestionDismissed, setRoutineSuggestionDismissed] = useState(() =>
    isRoutineSuggestionDismissedForProject(projectId)
  );
  const { locale } = useIntl();
  const { data, isLoading, isError } = useProjectFollowUpQuestions(projectId, {
    replyId,
    enabled,
    displayLanguage: locale,
  });

  useEffect(() => {
    setRoutineSuggestionDismissed(isRoutineSuggestionDismissedForProject(projectId));
  }, [projectId]);

  if (!enabled) return null;

  const questions = data?.questions ?? [];
  const routineMode = resolveRoutineSuggestionMode(questions);
  const routineChange =
    questions.find((question) => question.type === 'workflow_update')?.question.trim() ||
    undefined;
  const followUpQuestions = questions.filter(
    (question) => !ROUTINE_CARD_TYPES.has(question.type)
  );

  const dismissRoutineCard = (): void => {
    setRoutineSuggestionDismissed(true);
    dismissRoutineSuggestionForProject(projectId);
  };

  // Auto-shown under every settled reply, so an empty / errored
  // generation must render nothing rather than an "empty shell" caption
  // permanently sitting above the composer (mirrors Evidence's chips).
  if (
    !isLoading &&
    (isError ||
      (routineMode === null && followUpQuestions.length === 0) ||
      (routineSuggestionDismissed && followUpQuestions.length === 0))
  ) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-col gap-0.5', className)}
      data-testid="project-follow-up-questions"
    >
      {!isLoading && routineMode !== null && !routineSuggestionDismissed ? (
        <RoutineSuggestionCard
          mode={routineMode}
          change={routineChange}
          onCreate={() => {
            dismissRoutineCard();
            onSelect(
              routineMode === 'update'
                ? routineChange || UPDATE_WORKFLOW_COMPOSER_PREFILL
                : MAKE_AS_WORKFLOW_COMPOSER_PREFILL
            );
          }}
          onDismiss={dismissRoutineCard}
        />
      ) : null}
      {isLoading
        ? [0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2 p-2">
              <div className="size-3.5 shrink-0 animate-pulse rounded bg-background-secondary" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-background-secondary" />
            </div>
          ))
        : followUpQuestions.map((q, index) => (
            <Button
              key={`${index}-${q.question}`}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onSelect(q.question)}
              data-testid="project-follow-up-question"
              className="h-auto items-start justify-start gap-2 whitespace-normal py-2 text-left font-normal text-text-secondary hover:text-text-primary"
            >
              <span className="flex h-8 shrink-0 items-center">
                <CornerDownRight
                  aria-hidden="true"
                  className="text-text-tertiary rtl:-scale-x-100"
                />
              </span>
              <span className="min-w-0 break-words">{q.question}</span>
            </Button>
          ))}
    </div>
  );
};
